import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GeoChecklistResponse } from '../../../models/geo-checklist/GeoChecklistResponse';
import { toggleGeoChecklistTask } from '../../../services/geo-checklist/GeoChecklistService';
import { GET_GEO_CHECKLIST } from '../../query/geo-checklist/keys';

interface ToggleGeoChecklistTaskParams {
  projectId: number;
  checklistType: 'TOPICS_PROMPTS' | 'AI_VISIBILITY';
  taskId: string;
}

export const useToggleGeoChecklistTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, checklistType, taskId }: ToggleGeoChecklistTaskParams) =>
      toggleGeoChecklistTask(projectId, checklistType, taskId),

    onMutate: async ({ projectId, checklistType, taskId }) => {
      await queryClient.cancelQueries({ queryKey: [GET_GEO_CHECKLIST, projectId] });

      const previousData = queryClient.getQueryData<GeoChecklistResponse>([
        GET_GEO_CHECKLIST,
        projectId,
      ]);

      queryClient.setQueryData<GeoChecklistResponse>(
        [GET_GEO_CHECKLIST, projectId],
        (old) => {
          if (!old) return old;

          const currentSection = old[checklistType] ?? {};
          const updatedSection = { ...currentSection };

          if (taskId in updatedSection) {
            delete updatedSection[taskId];
          } else {
            updatedSection[taskId] = new Date().toISOString().slice(0, 10);
          }

          return {
            ...old,
            [checklistType]: updatedSection,
          };
        }
      );

      return { previousData };
    },

    onSuccess: (_data, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: [GET_GEO_CHECKLIST, projectId] });
    },

    onError: (_error, { projectId }, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([GET_GEO_CHECKLIST, projectId], context.previousData);
      }
    },
  });
};
